import React from "react";
import ReactDOM from "react-dom/client";
import { Store } from "@ludens-reklame/rubics-v3-sdk/store/types";
import { Section } from "@ludens-reklame/rubics-v3-sdk/section/types";
import { App } from "./app";
import { ErrorBoundary } from "./internal-components/error-boundary/error-boundary";
import Header from "./components/header/header";
import Footer from "./components/footer/footer";

// lazy loaded components
const Text = React.lazy(() => import("./components/text/text"));
const Image = React.lazy(() => import("./components/image/image"));
const Table = React.lazy(() => import("./components/table/table"));
const InfoBox = React.lazy(() => import("./components/info-box/info-box"));
const Hero = React.lazy(() => import("./components/hero/hero"));
const FormBanner = React.lazy(
  () => import("./components/form-banner/form-banner")
);
const ImageAndText = React.lazy(
  () => import("./components/image-and-text/image-and-text")
);
const SearchResults = React.lazy(
  () => import("./components/search-results/search-results")
);

const ImageList = React.lazy(
  () => import("./components/image-list/image-list")
);
const SelectedPages = React.lazy(
  () => import("./components/selected-pages/selected-pages")
);
const PageNavigation = React.lazy(
  () => import("./components/page-navigation/page-navigation")
);
const VerticalMenu = React.lazy(
  () => import("./components/vertical-menu/vertical-menu")
);
const ArticleIntro = React.lazy(
  () => import("./components/article-intro/article-intro")
);
const SearchBanner = React.lazy(
  () => import("./components/search-banner/search-banner")
);
const PageSearchBanner = React.lazy(
  () => import("./components/page-search-banner/page-search-banner")
);
const Pages = React.lazy(() => import("./components/pages/pages"));
const Embed = React.lazy(() => import("./components/embed/embed"));

declare const _STORE: Store;

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ErrorBoundary root={true}>
      <App store={_STORE} renderer={renderSection} />
    </ErrorBoundary>
  </React.StrictMode>
);

function renderSection(
  section: Section,
  children?: React.ReactNode
): React.ReactNode {
  let suspend: boolean = true;
  let Component: React.ComponentType<any>;
  const domElem = document.getElementById(section.name);

  switch (section.component) {
    case "Header":
      Component = Header;
      suspend = false;
      break;
    case "Footer":
      Component = Footer;
      suspend = false;
      break;
    case "Text":
      Component = Text;
      break;
    case "Image":
      Component = Image;
      break;
    case "Table":
      Component = Table;
      break;
    case "InfoBox":
      Component = InfoBox;
      break;
    case "Hero":
      Component = Hero;
      break;
    case "FormBanner":
      Component = FormBanner;
      break;
    case "ImageAndText":
      Component = ImageAndText;
      break;
    case "SearchResults":
      Component = SearchResults;
      break;
    case "ImageList":
      Component = ImageList;
      break;
    case "SelectedPages":
      Component = SelectedPages;
      break;
    case "PageNavigation":
      Component = PageNavigation;
      break;
    case "VerticalMenu":
      Component = VerticalMenu;
      break;
    case "ArticleIntro":
      Component = ArticleIntro;
      break;
    case "SearchBanner":
      Component = SearchBanner;
      break;
    case "PageSearchBanner":
      Component = PageSearchBanner;
      break;
    case "Pages":
      Component = Pages;
      break;
    case "Embed":
      Component = Embed;
      break;
    default:
      return null;
  }

  if (!suspend) {
    return (
      <ErrorBoundary>
        <Component {...section.props}>{children}</Component>
      </ErrorBoundary>
    );
  }

  return (
    <React.Suspense
      fallback={
        domElem ? (
          <div dangerouslySetInnerHTML={{ __html: domElem.innerHTML }} />
        ) : null
      }
    >
      <ErrorBoundary>
        <Component {...section.props}>{children}</Component>
      </ErrorBoundary>
    </React.Suspense>
  );
}
